import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'motion/react';
import { Bell, LogOut, Menu, Moon, Search, Settings, Sun, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button.js';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.js';
import { Badge } from '@/components/ui/badge.js';
import { Separator } from '@/components/ui/separator.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';
import { Breadcrumbs, type BreadcrumbItem } from '@/components/common/Breadcrumbs/Breadcrumbs.js';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown/index.js';
import { CommandPalette } from '@/components/layout/CommandPalette/index.js';
import { useNotifications } from '@/shared/hooks/useNotifications.js';
import { useTheme } from '@/shared/useTheme.js';
import { useLogoutMutation } from '@/features/auth/authApi.js';
import { logout as logoutAction } from '@/features/auth/authSlice.js';
import { baseApi } from '@/shared/api/baseApi.js';
import type { RootState } from '@/app/store.js';
import { cn, getInitials } from '@/lib/utils.js';
import type { HeaderProps } from './Header.types.js';

const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Главная',
  projects: 'Проекты',
  new: 'Создание',
  tasks: 'Задачи',
  rates: 'Расценки',
  materials: 'Материалы',
  teams: 'Команды',
  chat: 'Чат',
  profile: 'Профиль',
};

function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];
  if (segments.length === 1 && segments[0] === 'dashboard') {
    return [{ label: 'Главная', href: undefined }];
  }
  const items: BreadcrumbItem[] = [{ label: 'Главная', href: '/dashboard' }];
  let acc = '';
  segments.forEach((seg, idx) => {
    acc += '/' + seg;
    const label =
      ROUTE_LABELS[seg] ??
      (seg.length > 16 ? '#' + seg.slice(0, 8) : seg.charAt(0).toUpperCase() + seg.slice(1));
    items.push({ label, href: idx === segments.length - 1 ? undefined : acc });
  });
  return items;
}

export function Header({ onMenuClick, isMobile }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((s: RootState) => s.auth.user);
  const { unreadCount } = useNotifications();
  const { theme, toggleTheme } = useTheme();
  const [logoutMutation] = useLogoutMutation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [shortcutHint, setShortcutHint] = useState<'mac' | 'pc'>('pc');

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const isMac = /Mac|iPhone|iPad/i.test(navigator.platform);
    setShortcutHint(isMac ? 'mac' : 'pc');
  }, []);

  const breadcrumbs = useMemo(() => buildBreadcrumbs(location.pathname), [location.pathname]);

  const handleLogout = async () => {
    try {
      await logoutMutation().unwrap();
    } catch {
      // ignore
    }
    dispatch(logoutAction());
    dispatch(baseApi.util.resetApiState());
    toast.message('Вы вышли из аккаунта');
    navigate('/login');
  };

  const initials = getInitials(user?.name);

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-30 flex items-center gap-3 border-b border-border-subtle bg-background/70 backdrop-blur-xl px-3 sm:px-5',
          // В PWA на iPhone (black-translucent) контент начинается под статус-баром.
          'pt-[env(safe-area-inset-top)]',
          'h-[calc(3.5rem+env(safe-area-inset-top))] sm:h-[calc(4rem+env(safe-area-inset-top))]',
        )}
      >
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={onMenuClick} aria-label="Открыть меню">
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Breadcrumbs (desktop) */}
        <div className="hidden min-w-0 flex-1 lg:block">
          {breadcrumbs.length > 1 && <Breadcrumbs items={breadcrumbs} className="mb-0" />}
        </div>

        {/* Title fallback for mobile */}
        <div className="flex-1 truncate lg:hidden">
          <span className="font-display text-h4 font-semibold">
            {breadcrumbs[breadcrumbs.length - 1]?.label ?? 'ПростоСтрой'}
          </span>
        </div>

        {/* Search trigger (cmd+k) */}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className={cn(
            'hidden h-9 items-center gap-2 rounded-lg border border-border-subtle bg-card/40 px-3 text-small text-muted-foreground',
            'transition-colors hover:bg-card/70 hover:text-foreground hover:border-border-default',
            'sm:flex sm:w-[260px] lg:w-[320px]',
          )}
        >
          <Search className="h-4 w-4" />
          <span className="flex-1 text-left">Поиск...</span>
          <kbd className="rounded border border-border-subtle bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase text-muted-foreground">
            {shortcutHint === 'mac' ? '⌘ K' : 'Ctrl K'}
          </kbd>
        </button>

        {/* Mobile search icon */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setPaletteOpen(true)}
          className="sm:hidden"
          aria-label="Поиск"
        >
          <Search className="h-5 w-5" />
        </Button>

        {/* Theme toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Сменить тему">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={theme}
                  initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
                  animate={{ rotate: 0, opacity: 1, scale: 1 }}
                  exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
                  transition={{ duration: 0.2 }}
                  className="grid place-items-center"
                >
                  {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </motion.span>
              </AnimatePresence>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
          </TooltipContent>
        </Tooltip>

        {/* Notifications */}
        <Popover open={notifOpen} onOpenChange={setNotifOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" aria-label="Уведомления">
              <Bell className="h-5 w-5" />
              <AnimatePresence>
                {unreadCount > 0 && (
                  <motion.span
                    key="badge"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 22 }}
                    className="absolute -top-0.5 -right-0.5"
                  >
                    <Badge
                      variant="destructive"
                      className="h-4 min-w-[16px] rounded-full border-background px-1 text-[10px] font-semibold leading-none"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={8}
            className="w-[380px] p-0 max-w-[calc(100vw-2rem)]"
          >
            <NotificationDropdown onClose={() => setNotifOpen(false)} />
          </PopoverContent>
        </Popover>

        <Separator orientation="vertical" className="hidden h-6 sm:block" />

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="rounded-full transition-all hover:ring-2 hover:ring-primary/30 hover:ring-offset-2 hover:ring-offset-background"
              aria-label="Меню пользователя"
            >
              <Avatar className="h-9 w-9">
                {user?.avatarUrl ? (
                  <AvatarImage src={user.avatarUrl} alt={user.name ?? 'avatar'} />
                ) : null}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {user && (
              <>
                <DropdownMenuLabel className="normal-case tracking-normal">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-small font-semibold text-foreground">{user.name}</span>
                    <span className="truncate text-xs font-normal text-muted-foreground">
                      {user.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onSelect={() => navigate('/profile')}>
              <UserIcon className="text-muted-foreground" />
              Профиль
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/profile')}>
              <Settings className="text-muted-foreground" />
              Настройки
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
              onSelect={handleLogout}
            >
              <LogOut />
              Выйти
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
}
